export const TYPING_TEST_DURATION = 30;
export const TYPING_LINES_VISIBLE = 3;
export const TYPING_LINES_ADVANCE_AFTER = 2;
export const TYPING_WORDS_PER_LINE = 7;

export const DEFAULT_TYPING_PASSAGES = [
  "the patient needs medicine from the pharmacy store doctor prescription dosage tablet capsule syrup cream ointment injection dose morning evening daily weekly safe check verify record customer service stock inventory shelf label expiry batch supply order bill cash counter advice symptom fever pain cough cold vitamin health care delivery payment receipt quality brand generic price discount offer return replace storage temperature fridge box packet strip bottle water food before after meal night sleep rest blood pressure sugar diabetes allergy reaction side effect consult refer urgent emergency help assist explain guide trust honest clean hygiene mask glove",
  "customer asked for medicine at the pharmacy counter verify prescription dosage before supply tablet capsule syrup cream ointment injection check expiry batch label shelf stock inventory order bill cash payment receipt delivery address phone record name quality brand generic price discount offer return replace storage temperature fridge box packet strip bottle morning evening daily weekly safe symptom fever pain cough cold vitamin health care blood pressure sugar diabetes allergy reaction side effect consult refer urgent emergency help assist explain guide trust honest clean hygiene mask glove",
  "pharmacy store staff must check prescription dosage and advise patient on medicine tablet capsule syrup cream ointment injection dose morning evening daily weekly safe verify record customer service stock inventory shelf label expiry batch supply order bill cash counter symptom fever pain cough cold vitamin health care delivery payment receipt quality brand generic price discount offer return replace storage temperature fridge box packet strip bottle water food before after meal night sleep rest blood pressure sugar diabetes allergy reaction consult refer urgent emergency help assist explain guide trust honest clean hygiene mask glove",
  "doctor prescription requires correct dosage of medicine tablet capsule syrup cream ointment injection for patient care at pharmacy store counter verify label expiry batch shelf stock inventory supply order bill cash payment receipt customer service delivery address phone record name quality brand generic price discount offer return replace storage temperature fridge box packet strip bottle morning evening daily weekly safe symptom fever pain cough cold vitamin health blood pressure sugar diabetes allergy reaction side effect consult refer urgent emergency help assist explain guide trust honest clean hygiene mask glove",
];

export function resolveTypingPassages(passages) {
  const cleaned = (Array.isArray(passages) ? passages : [])
    .map((p) => String(p).trim())
    .filter(Boolean);
  return cleaned.length ? cleaned : [...DEFAULT_TYPING_PASSAGES];
}

const TYPING_WORDS = [
  "the", "patient", "medicine", "tablet", "doctor", "pharmacy", "store",
  "prescription", "dosage", "health", "care", "customer", "service", "stock",
  "inventory", "shelf", "label", "expiry", "batch", "supply", "order", "bill",
  "cash", "counter", "advice", "symptom", "fever", "pain", "cough", "cold",
  "vitamin", "capsule", "syrup", "cream", "ointment", "injection", "dose",
  "morning", "evening", "daily", "weekly", "safe", "check", "verify", "record",
  "name", "phone", "address", "delivery", "payment", "receipt", "quality",
  "brand", "generic", "price", "discount", "offer", "return", "replace",
  "storage", "temperature", "fridge", "box", "packet", "strip", "bottle",
  "water", "food", "before", "after", "meal", "night", "sleep", "rest",
  "blood", "pressure", "sugar", "diabetes", "allergy", "reaction", "side",
  "effect", "consult", "refer", "urgent", "emergency", "help", "assist",
  "explain", "guide", "trust", "honest", "clean", "hygiene", "mask", "glove",
];

function shuffle(array) {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function pickRandomTypingPassage(passages) {
  const pool = resolveTypingPassages(passages);
  return pool[Math.floor(Math.random() * pool.length)];
}

export function generateTypingPassage(minChars = 280, maxChars = 380) {
  const words = shuffle(TYPING_WORDS);
  const selected = [];
  let length = 0;

  for (const word of words) {
    const nextLength = selected.length === 0 ? word.length : length + 1 + word.length;
    if (nextLength > maxChars && selected.length > 0) break;
    selected.push(word);
    length = nextLength;
    if (length >= minChars) break;
  }

  return selected.join(" ");
}

/** Split passage into fixed word-count lines with character offsets in the full passage. */
export function buildPassageLines(passage, wordsPerLine = TYPING_WORDS_PER_LINE) {
  const words = passage.split(" ");
  const lines = [];
  let idx = 0;

  for (let i = 0; i < words.length; i += wordsPerLine) {
    const chunk = words.slice(i, i + wordsPerLine);
    const text = chunk.join(" ");
    const startIndex = idx;
    idx += text.length;
    const endIndex = idx;
    if (i + wordsPerLine < words.length) idx += 1;
    lines.push({ text, startIndex, endIndex });
  }

  return lines;
}

/** Rolling window: show 3 lines; after 2 lines typed, scroll so line 1 drops and next line appears. */
export function computeVisibleLineStart(
  typedLength,
  lines,
  visibleCount = TYPING_LINES_VISIBLE,
  advanceAfter = TYPING_LINES_ADVANCE_AFTER
) {
  if (!lines.length || lines.length <= visibleCount) return 0;

  let start = 0;
  const maxStart = lines.length - visibleCount;

  while (start < maxStart) {
    const advanceAt = lines[start + advanceAfter]?.startIndex;
    if (advanceAt != null && typedLength >= advanceAt) {
      start += 1;
    } else {
      break;
    }
  }

  return start;
}

export function compareTypedChars(target, typed) {
  const maxLen = Math.max(target.length, typed.length);
  const chars = [];

  for (let i = 0; i < maxLen; i += 1) {
    const targetChar = target[i] || "";
    const typedChar = typed[i] || "";

    if (i >= typed.length) {
      chars.push({ char: targetChar, status: "pending" });
    } else if (typedChar === targetChar) {
      chars.push({ char: typedChar, status: "correct" });
    } else {
      chars.push({ char: typedChar || targetChar, status: "incorrect" });
    }
  }

  return chars;
}

export function computeTypingMetrics(targetText, typedText, elapsedSeconds) {
  const duration = Math.max(elapsedSeconds, 1);
  let correctChars = 0;
  let incorrectChars = 0;

  const compareLength = Math.max(targetText.length, typedText.length);
  for (let i = 0; i < compareLength; i += 1) {
    const targetChar = targetText[i];
    const typedChar = typedText[i];

    if (typedChar === undefined) {
      break;
    }

    if (targetChar === undefined) {
      incorrectChars += 1;
    } else if (typedChar === targetChar) {
      correctChars += 1;
    } else {
      incorrectChars += 1;
    }
  }

  const totalChars = typedText.length;
  const wpm = Math.round(((correctChars / 5) / (duration / 60)) * 10) / 10;
  const accuracy = totalChars > 0
    ? Math.round((correctChars / totalChars) * 100)
    : 0;

  return {
    wpm,
    accuracy,
    correctChars,
    incorrectChars,
    totalChars,
    passage: targetText,
    typedText,
    durationSeconds: duration,
  };
}

export function computeLiveStats(targetText, typedText, elapsedSeconds) {
  if (!typedText.length || elapsedSeconds <= 0) {
    return { wpm: 0, accuracy: 0 };
  }

  const metrics = computeTypingMetrics(targetText, typedText, elapsedSeconds);
  return { wpm: metrics.wpm, accuracy: metrics.accuracy };
}
